import { jsonRequest } from '@/utils/jsonRequest'
import type { Count } from '@/types/generated'
import { createConsumer } from '@rails/actioncable'
import { writable } from 'svelte/store'
import type { Subscription } from '@rails/actioncable'
import type {
  OcrScan,
  ImportRunUpdate as ImportRun,
  SeriesImportRunUpdate as SeriesImportRun,
  LiveReloadEvent,
  InboxCountEvent,
  OcrScanEvent,
  ImportRunEvent,
  SeriesImportRunEvent,
} from '@/types/generated'
export type {
  OcrScan,
  OcrBook,
  ImportRunUpdate as ImportRun,
  SeriesImportRunUpdate as SeriesImportRun,
} from '@/types/generated'
export type ParsedData = NonNullable<OcrScan['parsed_data']>
import { router } from '@inertiajs/svelte'

export const inboxCount = writable(0)

export const ocrScanUpdates = writable<OcrScan | null>(null)
export const importRunUpdates = writable<ImportRun | null>(null)
export const seriesImportRunUpdates = writable<SeriesImportRun | null>(null)

let subscription: Subscription | null = null
let importRunSubscription: Subscription | null = null
let importRunId: number | null = null
let seriesImportRunSubscription: Subscription | null = null
let seriesImportRunSeriesId: number | null = null
let inboxCountLoaded = false

// Shared consumer instance - reused across all subscriptions
let sharedConsumer: ReturnType<typeof createConsumer> | null = null

function getConsumer() {
  if (!sharedConsumer) {
    sharedConsumer = createConsumer()
  }
  return sharedConsumer
}

export function initializeCable() {
  if (subscription) {
    return
  }

  const cable = getConsumer()

  subscription = cable.subscriptions.create('UserChannel', {
    received(data: InboxCountEvent | OcrScanEvent) {
      if (data.type === 'inbox_update' && data.inbox_count !== undefined) {
        inboxCount.set(data.inbox_count)
      } else if (data.type === 'ocr_scan_update' && data.ocr_scan) {
        ocrScanUpdates.set(data.ocr_scan)
      }
    },

    connected() {
      console.log('Connected to UserChannel')
    },

    disconnected() {
      console.log('Disconnected from UserChannel')
    },

    rejected() {
      console.error('Connection rejected - authentication failed')
    },
  })
}

export function disconnectCable() {
  if (subscription) {
    console.log('Disconnecting from UserChannel')
    subscription.unsubscribe()
    subscription = null
  }
}

export async function loadInboxCount() {
  if (inboxCountLoaded) return

  try {
    const data = await jsonRequest<Count>('/inbox/count')
    inboxCount.set(data.count)
    inboxCountLoaded = true
  } catch {
    // Leave the count retryable after a failed background request.
  }
}

export function subscribeToImportRun(id: number) {
  // Skip if already subscribed to this import run
  if (importRunSubscription && importRunId === id) {
    return
  }

  if (importRunSubscription) {
    importRunSubscription.unsubscribe()
  }

  importRunId = id
  const cable = getConsumer()

  importRunSubscription = cable.subscriptions.create(
    { channel: 'ImportRunsChannel', id },
    {
      received(data: ImportRunEvent) {
        if (data.type === 'import_run_update' && data.import_run) {
          importRunUpdates.set(data.import_run)
        }
      },

      connected() {
        console.log(`Connected to ImportRunsChannel for import run ${id}`)
      },

      disconnected() {
        console.log(`Disconnected from ImportRunsChannel for import run ${id}`)
      },

      rejected() {
        console.error('ImportRunsChannel connection rejected')
      },
    }
  )
}

export function unsubscribeFromImportRun() {
  if (importRunSubscription) {
    console.log('Unsubscribing from ImportRunsChannel')
    importRunSubscription.unsubscribe()
    importRunSubscription = null
    importRunId = null
    importRunUpdates.set(null)
  }
}

export function subscribeToSeriesImportRun(seriesId: number) {
  // Skip if already subscribed to this series
  if (seriesImportRunSubscription && seriesImportRunSeriesId === seriesId) {
    return
  }

  if (seriesImportRunSubscription) {
    seriesImportRunSubscription.unsubscribe()
  }

  seriesImportRunSeriesId = seriesId
  const cable = getConsumer()

  seriesImportRunSubscription = cable.subscriptions.create(
    { channel: 'SeriesImportRunsChannel', series_id: seriesId },
    {
      received(data: SeriesImportRunEvent) {
        if (data.type === 'series_import_run_update' && data.import_run) {
          seriesImportRunUpdates.set(data.import_run)
        }
      },

      connected() {
        console.log(
          `Connected to SeriesImportRunsChannel for series ${seriesId}`
        )
      },

      disconnected() {
        console.log(
          `Disconnected from SeriesImportRunsChannel for series ${seriesId}`
        )
      },

      rejected() {
        console.error('SeriesImportRunsChannel connection rejected')
      },
    }
  )
}

export function unsubscribeFromSeriesImportRun() {
  if (seriesImportRunSubscription) {
    console.log('Unsubscribing from SeriesImportRunsChannel')
    seriesImportRunSubscription.unsubscribe()
    seriesImportRunSubscription = null
    seriesImportRunSeriesId = null
    seriesImportRunUpdates.set(null)
  }
}

export function subscribeToLiveReload(stream: string): Subscription {
  const cable = getConsumer()

  return cable.subscriptions.create(
    { channel: 'LiveUpdatesChannel', stream },
    {
      received(data: LiveReloadEvent) {
        if (data.reload?.length) {
          router.reload({ only: data.reload })
        }
      },

      connected() {
        console.log(`Connected to LiveUpdatesChannel for ${stream}`)
      },

      disconnected() {
        console.log(`Disconnected from LiveUpdatesChannel for ${stream}`)
      },

      rejected() {
        console.error(`LiveUpdatesChannel rejected for ${stream}`)
      },
    }
  )
}
